import { setSidebarOpenTab } from '@/atoms/persistedPreferencesAtoms';
import { sessionListSearchValueAtom } from '@/atoms/sideNavAtoms';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import { offlineGatingMessages } from '@/components/offline-recording/offline-gating-messages';
import SessionSettingsPopover from '@/features/consolidatedSessionsList/components/sessionSettingsPopover/sessionSettingsPopover';
import { consolidatedSessionsListMessages } from '@/features/consolidatedSessionsList/consolidatedSessionsList.messages';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { Button } from '@heidi/design-system/button';
import { SearchInput } from '@heidi/design-system/search-input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { isUploadSessionScheduleModalOpenAtom } from '@heidi/session';
import { useAtom, useSetAtom } from 'jotai';
import { ArrowUpToLine, PanelLeftClose, Search, X } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const SessionListHeader = memo(() => {
  const intl = useIntl();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useAtom(sessionListSearchValueAtom);
  const isSearchVisible = isSearchOpen || searchValue.trim().length > 0;
  const { checkRBACPermission } = useRBACPermissionCheck();
  const canCreateSessions = useMemo(() => {
    return checkRBACPermission(
      PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_CREATE,
      PERMISSION_SUBJECTS.SESSION_CREATE
    );
  }, [checkRBACPermission]);
  const setIsUploadSessionScheduleModalOpen = useSetAtom(
    isUploadSessionScheduleModalOpenAtom
  );
  // Upload (a server-backed schedule flow) and search are greyed out while
  // offline-gated per the offline read-only policy. Session browsing and the
  // client-side clear stay available offline.
  const { shouldGateControls: isOfflineGated } = useOfflineGating();
  const offlineDisabledTooltip = intl.formatMessage(
    offlineGatingMessages.notAvailableOffline
  );
  const clearSearchLabel = intl.formatMessage({
    defaultMessage: 'Clear search',
    id: '4YJHutR26p',
  });

  const handleCollapse = useCallback(() => {
    setSidebarOpenTab(undefined);
  }, []);

  const searchToggleButton = (
    <Button
      variant="panel_ghost"
      size="xxs"
      iconOnly
      disableAutomaticSvgSizing
      disabled={isOfflineGated}
      onClick={isOfflineGated ? undefined : () => setIsSearchOpen(!isSearchOpen)}
      data-active={isSearchVisible}
      className="text-text-secondary"
    >
      <Search className="size-4 shrink-0" aria-hidden="true" />
    </Button>
  );

  const searchInput = (
    <SearchInput
      placeholder={intl.formatMessage(consolidatedSessionsListMessages.searchSessions)}
      value={searchValue}
      disabled={isOfflineGated}
      onChange={isOfflineGated ? undefined : (e) => setSearchValue(e.target.value)}
      autoFocus={isSearchOpen && !isOfflineGated}
      clearAriaLabel={clearSearchLabel}
      accessoryEnd={
        isOfflineGated && searchValue ? (
          // SearchInput hides its built-in clear while disabled, but clearing is
          // client-side (in-memory atom) and must stay live offline.
          <Button
            iconOnly
            variant="ghost"
            size="xxs"
            onClick={() => setSearchValue('')}
            className="text-text-secondary"
            aria-label={clearSearchLabel}
          >
            <X size={16} aria-hidden="true" />
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-0.5">
        {/* Left: Upload Schedule action */}
        {canCreateSessions && (
          <OfflineGatedTooltip isOfflineGated={isOfflineGated}>
            <Button
              variant="secondary"
              size="sm"
              data-testid="sessions-panel-upload-button"
              disabled={isOfflineGated}
              onClick={
                isOfflineGated
                  ? undefined
                  : () => setIsUploadSessionScheduleModalOpen(true)
              }
            >
              <ArrowUpToLine className="shrink-0" aria-hidden="true" />
              <FormattedMessage {...consolidatedSessionsListMessages.uploadSchedule} />
            </Button>
          </OfflineGatedTooltip>
        )}

        {/* Right: Settings, Search, Collapse with gap-2 */}
        <div className="flex items-center gap-0.5">
          <SessionSettingsPopover />
          <Tooltip>
            <TooltipTrigger asChild>
              {isOfflineGated ? (
                // span wrapper keeps the disabled button hoverable so the offline
                // tooltip still shows; only while gated (an unconditional span
                // would suppress the enabled button's aria wiring).
                <span className="inline-flex">{searchToggleButton}</span>
              ) : (
                searchToggleButton
              )}
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isOfflineGated ? (
                offlineDisabledTooltip
              ) : (
                <FormattedMessage
                  {...consolidatedSessionsListMessages.searchSessionsTooltip}
                />
              )}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="panel_ghost"
                size="xxs"
                iconOnly
                disableAutomaticSvgSizing
                onClick={handleCollapse}
                className="text-text-secondary"
              >
                <PanelLeftClose className="size-4 shrink-0" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <FormattedMessage
                {...consolidatedSessionsListMessages.collapseSessionsTooltip}
              />
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Search input row - expands below header when open */}
      {isSearchVisible &&
        (isOfflineGated ? (
          <OfflineGatedTooltip isOfflineGated className="w-full">
            {searchInput}
          </OfflineGatedTooltip>
        ) : (
          searchInput
        ))}
    </div>
  );
});

SessionListHeader.displayName = 'SessionListHeader';
export default SessionListHeader;
